import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUniversityApplicants, getPrograms } from '../services/api';
import { EdFiLogo } from '../components/EdFiLogo';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Users, FileText, TrendingUp, DollarSign, LogOut, Building2,
  CheckCircle, Clock, XCircle, Eye, GraduationCap, BookOpen, ArrowRight
} from 'lucide-react';

const UniversityDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, rejected: 0, totalAmount: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [activeTab, setActiveTab] = useState('applicants');

  useEffect(() => {
    if (user?.role !== 'university' && user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [applicantsRes, programsRes] = await Promise.all([
        api.get('/university/applicants'),
        getPrograms(user?.university_id ? { university_id: user.university_id } : {})
      ]);
      const data = applicantsRes.data;
      setApplicants(data);
      setPrograms(programsRes.data.programs || []);
      
      const approved = data.filter(a => a.status === 'approved' || a.status === 'disbursed').length;
      const pending = data.filter(a => ['submitted', 'under_review', 'documents_required'].includes(a.status)).length;
      const rejected = data.filter(a => a.status === 'rejected').length;
      const totalAmount = data.filter(a => a.amount_approved).reduce((sum, a) => sum + a.amount_approved, 0);
      
      setStats({ total: data.length, approved, pending, rejected, totalAmount });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewStudentProfile = async (studentId) => {
    try {
      const response = await api.get(`/university/student/${studentId}`);
      setStudentProfile(response.data);
      setShowProfileModal(true);
    } catch (error) {
      console.error('Error fetching student profile:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(price);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Borrador', className: 'bg-slate-100 text-slate-700' },
      submitted: { label: 'Enviada', className: 'bg-blue-100 text-blue-700' },
      under_review: { label: 'En revision', className: 'bg-amber-100 text-amber-700' },
      approved: { label: 'Aprobada', className: 'bg-green-100 text-green-700' },
      rejected: { label: 'Rechazada', className: 'bg-red-100 text-red-700' },
      documents_required: { label: 'Docs pendientes', className: 'bg-orange-100 text-orange-700' },
      disbursed: { label: 'Desembolsado', className: 'bg-purple-100 text-purple-700' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-slate-100 text-slate-700' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getModalityLabel = (modality) => {
    const labels = { 'online': 'En linea', 'presencial': 'Presencial', 'hibrido': 'Hibrido', 'virtual': 'Virtual' };
    return labels[modality] || modality;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-brand-blue text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <EdFiLogo size="sm" linkTo="/" className="brightness-0 invert" />
              <Badge className="bg-white/20 text-white">Universidad</Badge>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-white/80">{user?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="uni-logout">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900" data-testid="uni-dashboard-title">Panel de Universidad</h1>
          <p className="text-slate-600">Gestiona tus programas y solicitudes de credito</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="uni-programs-count">{programs.length}</p>
                  <p className="text-xs text-slate-600">Programas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-brand-blue" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-xs text-slate-600">Solicitudes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                  <p className="text-xs text-slate-600">Aprobadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                  <p className="text-xs text-slate-600">En Proceso</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xl font-bold text-purple-600">{formatPrice(stats.totalAmount)}</p>
                  <p className="text-xs text-slate-600">Monto Aprobado</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="applicants" data-testid="tab-applicants">Solicitudes</TabsTrigger>
            <TabsTrigger value="programs" data-testid="tab-programs">Mis Programas</TabsTrigger>
          </TabsList>

          {/* Applicants Tab */}
          <TabsContent value="applicants">
            <Card>
              <CardHeader>
                <CardTitle>Solicitudes de Alumnos</CardTitle>
              </CardHeader>
              <CardContent>
                {applicants.length === 0 ? (
                  <div className="text-center py-12">
                    <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900">No hay solicitudes</h3>
                    <p className="text-slate-600">Aun no hay alumnos que hayan aplicado a tus programas</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Alumno</TableHead>
                        <TableHead>Programa</TableHead>
                        <TableHead className="text-right">Monto Solicitado</TableHead>
                        <TableHead className="text-right">Monto Aprobado</TableHead>
                        <TableHead className="text-center">Score</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead>Fecha</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {applicants.map((app) => (
                        <TableRow key={app.application_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{app.student?.name || 'N/A'}</p>
                              <p className="text-sm text-slate-500">{app.student?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell><p className="font-medium">{app.program?.name}</p></TableCell>
                          <TableCell className="text-right">{formatPrice(app.amount_requested)}</TableCell>
                          <TableCell className="text-right">{app.amount_approved ? formatPrice(app.amount_approved) : '-'}</TableCell>
                          <TableCell className="text-center">{app.credit_score || '-'}</TableCell>
                          <TableCell>{getStatusBadge(app.status)}</TableCell>
                          <TableCell>{formatDate(app.created_at)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => viewStudentProfile(app.user_id)} data-testid={`view-student-${app.user_id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Programs Tab - Marketplace View */}
          <TabsContent value="programs">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle data-testid="uni-programs-title">Catalogo de Programas</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">Programas que ofreces con financiamiento Ed.Fi</p>
                </div>
                <Badge variant="outline" className="text-brand-blue border-brand-blue">
                  {programs.length} programas
                </Badge>
              </CardHeader>
              <CardContent>
                {programs.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900">Sin programas registrados</h3>
                    <p className="text-slate-600">Contacta al administrador para agregar tus programas</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {programs.map((prog) => (
                      <div key={prog.program_id} className="border border-slate-200 rounded-xl p-5 card-hover" data-testid={`uni-program-${prog.program_id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <Badge variant="outline" className="text-brand-blue border-brand-blue/30">{prog.category}</Badge>
                          <Badge className="bg-slate-100 text-slate-600">{getModalityLabel(prog.modality)}</Badge>
                        </div>
                        <h3 className="text-base font-bold text-slate-900 mb-2 line-clamp-2">{prog.name}</h3>
                        {prog.description && (
                          <p className="text-sm text-slate-500 mb-3 line-clamp-2">{prog.description}</p>
                        )}
                        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Clock className="w-4 h-4" />
                            <span>{prog.duration_months}m</span>
                          </div>
                          <span className="text-lg font-bold text-brand-blue">{formatPrice(prog.price)}</span>
                        </div>
                        <div className="mt-3 p-2 bg-brand-blue/5 rounded-lg">
                          <div className="flex justify-between text-xs">
                            <span className="text-slate-500">Ed.Fi paga (80%)</span>
                            <span className="font-semibold text-brand-blue">{formatPrice(prog.price * 0.8)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Student Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Perfil del Alumno</DialogTitle>
          </DialogHeader>
          {studentProfile && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-brand-blue/10 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold text-brand-blue">{studentProfile.student?.name?.charAt(0)}</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{studentProfile.student?.name}</h3>
                  <p className="text-slate-600">{studentProfile.student?.email}</p>
                  <p className="text-slate-500">{studentProfile.student?.phone || 'Sin telefono'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Card><CardContent className="pt-4"><p className="text-sm text-slate-500">Total Solicitudes</p><p className="text-2xl font-bold">{studentProfile.total_applications}</p></CardContent></Card>
                <Card><CardContent className="pt-4"><p className="text-sm text-slate-500">Aprobadas</p><p className="text-2xl font-bold text-green-600">{studentProfile.approved_applications}</p></CardContent></Card>
                <Card><CardContent className="pt-4"><p className="text-sm text-slate-500">Monto Solicitado</p><p className="text-xl font-bold">{formatPrice(studentProfile.total_amount_requested)}</p></CardContent></Card>
                <Card><CardContent className="pt-4"><p className="text-sm text-slate-500">Monto Aprobado</p><p className="text-xl font-bold text-green-600">{formatPrice(studentProfile.total_amount_approved)}</p></CardContent></Card>
              </div>
              <div>
                <h4 className="font-semibold mb-3">Solicitudes</h4>
                <div className="space-y-2">
                  {studentProfile.applications?.map((app) => (
                    <div key={app.application_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">{app.program?.name}</p>
                        <p className="text-sm text-slate-500">{formatPrice(app.amount_requested)}</p>
                      </div>
                      {getStatusBadge(app.status)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UniversityDashboard;
